import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CURRENT_USER_COMPANY, CURRENT_USER_ROLE } from '../../hooks/useLogin';
import React, { useCallback, useState } from 'react';
import { type User, type Company } from '../../utils/type';
import { userDialogSx } from './styles';
import { setTimeZone } from '../../hooks/useTimeZone';
import { getCompany, resetLogin } from '../../api/apis';
import UserDialog from './UserDialog';
import MUIDataTable, { type MUIDataTableOptions } from 'mui-datatables';
// import { ThemeProvider } from "@mui/styles";
import { createTheme, ThemeProvider, responsiveFontSizes } from '@mui/material/styles';
import FormControlLabel from '@mui/material/FormControlLabel';
import { TokenExpiredError } from 'src/api/error';
import { useHistory } from 'react-router-dom';
import { ROUTES } from 'src/utils/constant';
declare module '@mui/material/styles' {
  type Components = Record<string, any>;
}

interface Props {
  users: User[];
  setUsers: (users: User[]) => void;
}

export const UserList = (props: Props) => {
  const { t } = useTranslation();
  const { users, setUsers } = props;
  const [companies, setCompanies] = useState<Company[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const isDepartmentAdmin = sessionStorage.getItem(CURRENT_USER_ROLE) === 'department-admin';
  const currentCompany = sessionStorage.getItem(CURRENT_USER_COMPANY);

  const attachCompany = (comps: Company[]) => {
    for (const user of users) {
      if (user.company_id) {
        const company = comps.find((company) => company.cid === user.company_id);
        if (company) {
          user.company = company.company_name;
        }
      }
    }
  };

  React.useEffect(() => {
    if (isDepartmentAdmin) {
      getCompany(currentCompany).then((res) => {
        setCompanies([res.data.company]);
        attachCompany([res.data.company]);
        setDepartments(res.data.company.departments);
      });
    } else {
      getCompany().then((res) => {
        setCompanies(() => {
          attachCompany(res.data.company);
          return res.data.company;
        });

        if (departments.length === 0) {
          for (const company of res.data.company) {
            setDepartments((departments) => [...departments, ...company.departments]);
          }
        }
      });
    }
  }, [users]);

  const [resetUser, setResetUser] = useState<User | null>(null);
  const [editUsers, setEditUsers] = useState<User | null>(null);
  const [isDialogVisible, toggleDialog] = useState(false);
  const options: MUIDataTableOptions = {
    filter: true,
    filterType: 'multiselect',
    responsive: 'vertical',
    fixedHeader: true,
    print: false,
    viewColumns: false,
    fixedSelectColumn: false,
    tableBodyHeight: '700px',
    selectableRows: 'none',
    download: false,
    rowsPerPage: 20,
    rowsPerPageOptions: [20, 30, 50, 100],
    textLabels: {
      body: {
        noMatch: 'Now loading user list',
      },
      filter: {
        all: t('All'),
        title: t('FILTERS'),
        reset: t('RESET'),
      },
      pagination: {
        next: t('Next Page'),
        previous: t('Previous Page'),
        rowsPerPage: t('Rows per page') + ':',
        displayRows: t('of'),
      },
    },
    onFilterChange (changedColumn, filterList, type, changedColumnIndex, displayData) {
      if (changedColumn === 'company') {
        const selectedCompanies = companies.filter((company: Company) => filterList[changedColumnIndex].includes(company.company_name));
        const availableDepartments = selectedCompanies.map((company: Company) => company.departments).flat();
        setDepartments(availableDepartments);
      }
    },
  };
  let theme = createTheme();
  theme = responsiveFontSizes(theme);
  function getMuiTheme () {
    return createTheme({
      components: {
        MuiTableCell: {
          styleOverrides: {
            root: {
              fontSize: '16px',
              padding: '16px 8px 16px 10px',
            },
            head: {
              background: '#006EC2 !important',
              color: '#fff !important',
              fontSize: '16px',
              '& .MUITableSortLabel-icon': { color: '#fff !important' },
            },
            footer: {
              background: '#fff !important',
            },
            stickyHeader: {
              color: '#fff !important',
              border: '1px #006EC2 solid',
              background: '#006EC2 !important',
            },
          },
        },
        MuiTableRow: {
          styleOverrides: {
            root: {
              '&:nth-of-type(odd)': {
                background: '#E5F0F9',
              },
              '&:nth-of-type(even)': {
                background: '#F5F9FD',
              },
              // hide last border
              '&:last-child td, &:last-child th': {
                border: 0,
              },
            },
            head: {
              fontSize: '16px',
              background: '#006EC2 !important',
              color: '#fff !important',
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              fontSize: '16px',
              'div div': {
                color: '#fff !important',
              },
            },
          },
        },
      },
    });
  }

  const columns = [
    {
      name: 'uid',
      label: 'UID',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
            <div style={{ width: '150px', overflow: 'hidden' }}>
              {value}
            </div>
          );
        },
      },
    },
    {
      name: 'uid',
      label: t('Edit'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          function isUser (Alluser: any) {
            return Alluser?.uid === value;
          }
          const user = users.find(isUser);
          return (
            <div style={{
              display: isDepartmentAdmin && user.role === 'department-admin' ? 'none' : 'flex',
              width: '89px',
              padding: '0px',
            }}>
              <IconButton className="iconEdit" size='small' onClick={() => {
                if (user) {
                  setEditUsers(user);
                  toggleDialog(true);
                }
              }}>
                <i className="bi bi-pencil-square" style={{ color: '#006EC2' }}></i>
              </IconButton>
              <IconButton className="iconEdit" size='small' onClick={() => { if (user) setResetUser(user); }}>
                <i className="bi bi-lock-fill" style={{ color: '#006EC2' }}></i>
              </IconButton>
            </div>
          );
        },
      },
    },
    {
      name: 'status',
      label: t('Status'),
      options: {
        filter: true,
        sort: true,
        filterOptions: {
          names: ['active', 'inactive'],
          logic: (status: string, filters: any) => {
            if (filters.length) return !filters.includes(status);
            return false;
          },
          renderValue: (value: string) => {
            return t(value.toUpperCase());
          },
        },
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
            <FormControlLabel
              label={''}
              value={value}
              style={{ width: '105px', padding: '0px' }}
              control={
                <Button style={{
                  padding: '0',
                  borderRadius: '16px',
                  fontSize: '12px',
                  backgroundColor: value === 'inactive' ? 'rgba(0,0,0,0.38)' : '#006EC2',
                }}
                variant="contained" size="small" onClick={() => {
                  value = value === 'inactive' ? 'active' : 'inactive';
                }} > {value === 'inactive' ? t('INACTIVE') : t('ACTIVE')} </Button>

              }
              onChange={ event => {
                const { target } = event;
                if (target) updateValue(target);
              }
              }
            />
          );
        },
        customFilterListOptions: {
          render: (value: string) => {
            return t(value.toUpperCase());
          },
        },
      },
    },
    {
      name: 'tel',
      label: t('Tel'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
            <div className='hovertext' data-hover={value} >
                <div style={{ width: '110px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {value}
                </div>
            </div>
          );
        },
      },
    },
    {
      name: 'location',
      label: t('Location'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
          <div className='hovertext' data-hover={value} >
            <div style={{ width: '90px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {value}
            </div>
          </div>
          );
        },
      },
    },
    {
      name: 'jobtype',
      label: t('Job type'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
            <div style={{ width: '90px' }}>
              {t(value)}
            </div>
          );
        },
      },
    },
    {
      name: 'role',
      label: t('Role'),
      options: {
        filter: true,
        sort: true,
        filterOptions: {
          names: ['platform-admin', 'division-admin', 'trainee'],
          logic: (role: string, filters: any) => {
            if (role === 'department-admin' && filters.includes('division-admin')) return !['platform-admin', 'department-admin', 'trainee'].includes(role);
            if (filters.length) return !filters.includes(role);
            return false;
          },
          renderValue: (value: string) => {
            return t(value === 'department-admin' ? 'division-admin' : value);
          },
        },
      },
      customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
        return (
          <div style={{ width: '140px' }}>
            {t(value === 'department-admin' ? 'division-admin' : value)}
          </div>
        );
      },
      customFilterListOptions: {
        render: (value: string) => {
          return t(value === 'department-admin' ? 'division-admin' : value);
        },
        customFilterListOptions: {
          render: (value: string) => {
            return t(value === 'department-admin' ? 'division-admin' : value);
          },
        },
      },
    },
    {
      name: 'company',
      label: t('Company'),
      options: {
        filter: true,
        sort: true,
        filterOptions: {
          names: companies.map((company: Company) => company.company_name).flat(),
        },
      },
    },
    {
      name: 'department',
      label: t('Department'),
      options: {
        filter: true,
        sort: true,
        filterOptions: {
          names: departments,
          logic: (department: string, filters: any) => {
            if (filters.length) {
              const departments = department.split('!');
              const filteredArray = departments.filter(value => filters.includes(value));
              return !filteredArray.length;
            }
            return false;
          },
        },
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
              <div style={{ maxWidth: '130px', wordWrap: 'break-word' }}>
                {value ? value.replaceAll('!', ', ') : '-'}
              </div>
          );
        },
      },
    },
    {
      name: 'login',
      label: t('Last login'),
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
            <div style={{ width: '120px', wordWrap: 'break-word' }}>
              {value ? setTimeZone(value, 'Hongkong') : '-'}
            </div>
          );
        },
      },
    },
  ];

  const deleteUser = useCallback((deletUser: any) => {
    console.log(users, 'deletUser', deletUser);
    if (users.length) {
      const deletIndex = users.findIndex((user) => user.email === deletUser);
      if (deletIndex !== -1) users.splice(deletIndex, 1);
      console.log(deletIndex, users.length);
    }
  }, [users]);

  const editUser = useCallback((editedUser: any) => {
    if (users.length) {
      const editIndex = users.findIndex((user) => user.email === editedUser.email);
      if (editIndex !== -1) {
        users[editIndex] = editedUser;
        const currentCompany = companies.find((company) => company.cid === editedUser.company_id);
        if (currentCompany) {
          users[editIndex].company = currentCompany.company_name;
        }

        setUsers([...users]);
      }
    }
  }, [users]);

  return (
    <main
      style={{
        display: 'flex',
        position: 'inherit',
      }}
    >

        <ThemeProvider theme={theme}>
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title={' '}
              data={users}
              columns={columns}
              options={options}
            />
          </ThemeProvider>
        </ThemeProvider>

      {resetUser && <ResetDialog user={resetUser} onClose={() => { setResetUser(null); }} />}
      {isDialogVisible && <UserDialog userData={editUsers} onClose={() => { toggleDialog(false); }} afterEditUser={editUser} afterDeleteUser={deleteUser}/>}
    </main>
  );
};

const ResetDialog: React.FC<{ user: User; onClose: () => void; }> = ({ user, onClose }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [newPassword, setNewPassword] = useState<string | null>(null);

  const onReset = async () => {
    resetLogin({ email: user.email }).then(response => {
      console.log(response);
      if (response.data?.login?.password) setNewPassword(response.data.login.password);
    })
      .catch(err => {
        if (err instanceof TokenExpiredError) {
          history.push(ROUTES.login);
        }
      });
  };

  const onConfirm = useCallback(() => {
    if (newPassword) {
      onClose();
    } else {
      onReset();
    }
  }, [newPassword, onClose, onReset]);

  const isResetSuccess = newPassword !== null;

  return (
    <Dialog open={true} sx={userDialogSx} onClose={onClose} maxWidth="md">
      <DialogTitle>
        {isResetSuccess ? t('Reset password successfully') : t('Reset password')} <i className="bi bi-x" onClick={onClose} />
      </DialogTitle>
      <DialogContent>
        <div className="confirm-row" style={{ color: '#006EC2', justifyContent: 'center', fontWeight: 'bold' }}>
          {t('Reset this user password')}
        </div>
        <div className="confirm-row">
          <span>UID: </span>
          <span>{user.uid}</span>
        </div>
        {isResetSuccess && (
          <div className="confirm-row">
            <span>{t('Password')}:</span>
            <span className="password-box">{newPassword}</span>
            <Button variant="contained" onClick={async () => { await navigator.clipboard.writeText(newPassword); }}>
              {t('Copy')}
            </Button>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <div>
          {!isResetSuccess && (
            <Button variant="contained" onClick={onClose} style={{ marginRight: '16px' }}>
              {t('Cancel')}
            </Button>
          )}
          <Button variant="contained" onClick={onConfirm}>
            {isResetSuccess ? t('Close') : t('Confirm')}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};
